import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {VehicleInterface} from "../../../services/interface/query/vehicle-interface";
import * as IconMaterial from "@mui/icons-material";
import {ItemInfo, ItemInfoSpouse, ItemNumber} from "../../components/ViewData";
import {Person} from "../../../services/interface/query/person";
import {useFindVehicles} from "../../../services/apis/apiVehicles";
import NotPermissionComponent from "../NotPermissionComponent";
import NoRecords from "../../components/NoRecords";

interface Props {
    person?: Person;
}

export default function Vehicles({person}: Props) {
    const {t} = useTranslation("global") as { t: (key: string) => string };
    const {
        mutate: getVehiclesInformation,
        data: successPersonVehiclesInformation,
        error: errorPersonVehiclesInformation,
        isPending: loadingPersonVehiclesInformation,
    } = useFindVehicles()

    const [list, setList] = React.useState<VehicleInterface[] | undefined>()
    const styleIcon = 'max-w-[20px] max-h-[20px]'

    useEffect(() => {
        if (person && person.identification) {
            getVehiclesInformation({identifier: person.identification, msj: t('vehicles')})
        }
    }, [person]);

    useEffect(() => {
        if (successPersonVehiclesInformation) {
            setList(successPersonVehiclesInformation)
        }
    }, [successPersonVehiclesInformation]);

    function getType(type: string | number | undefined) {
        switch (type) {
            case 'CROSS':
            case 'MOTOCICLETA':
                return <IconMaterial.TwoWheeler className={styleIcon}/>
            case 'HIBRIDO-A':
            case 'HIBRIDO-J':
            case 'ELECTRICO-M':
            case 'ELECTRICO-C':
            case 'ELECTRICO-A':
                return <IconMaterial.ElectricCar className={styleIcon}/>
            case 'OMNIBUS':
            case 'BUS':
            case 'BUSETA':
            case 'AUTOBUS':
                return <IconMaterial.DirectionsBus className={styleIcon}/>
            case 'TAXI':
                return <IconMaterial.LocalTaxi className={`${styleIcon} mt-[-2px]`}/>
            default:
                return <IconMaterial.DirectionsCar className={`${styleIcon} mt-[-2px]`}/>
        }
    }

    function ItemClass(type: string | number | undefined) {
        return (
            <div className={'ml-auto flex items-center justify-center rounded-[4px] ' +
                'w-[30px] h-[25px] bg-header text-white'}>
                {getType(type)}
            </div>
        )
    }

    function RenderVehicles() {
        if (list && list.length > 0) {
            return (
                <>
                    {list.map((item, index) => (
                        <div key={index}>
                            <div className={'flex flex-row'}>
                                <h5>{`${t('register')} #${index + 1}`}</h5>
                                {ItemClass(item.carClassType)}
                            </div>
                            <div className={'mt-1 custom-card py-3 px-3 flex flex-col md:flex-row gap-2 items-center'}>
                                <div className={'w-full md:w-[70%] flex flex-col gap-1'}>
                                    <div className={'grid sm:grid-cols-2 lg:grid-cols-3 gap-2'}>
                                        <ItemInfo label={t('class')} text={item.carClassType}/>
                                        <ItemInfo label={t('plate')} text={item.carIdentifierPlate}/>
                                        <ItemInfo label={t('chassis')} text={item.carIdentifierChassisCode}/>
                                    </div>
                                    <div className={`border border-t-1 border-t-secondary border-transparent 
                                pt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2`}>
                                        <ItemInfoSpouse label={t('brand')} text={item.carBrand}/>
                                        <ItemInfoSpouse classNameContent={'md:col-span-2'}
                                                        label={t('model')} text={item.carModel}/>
                                        <ItemInfoSpouse label={t('type')} text={item.carVehicleType}/>
                                        <ItemInfoSpouse label={t('province')} text={item.regCarProvince}/>
                                        <ItemInfoSpouse label={t('service')} text={item.carServiceType}/>
                                    </div>
                                </div>
                                <div
                                    className={'w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 md:w-[30%] gap-2'}>
                                    <ItemNumber label={t('cylinder')} value={item.carEngineDisplacement}/>
                                    <ItemNumber label={t('year')} value={`${item.carYear}`}/>
                                    <ItemNumber label={t('use-full-load')} value={`${item.carPayload}`}/>
                                    <ItemNumber label={t('registered')} value={`${item.regRegisteredYear}`}/>
                                    <ItemNumber label={t('appraisal')} value={`$${item.valValuation}`}/>
                                    <ItemNumber label={t('commercial-price')} value={`$${item.valPrice ? item.valPrice : ''}`}/>
                                </div>
                            </div>
                        </div>
                    ))}
                </>)
        } else {
            return <NoRecords/>
        }
    }

    return (
        <div className={'flex flex-col gap-3'}>
            <h3 className={'capitalize'}>{t('vehicles')}</h3>
            <NotPermissionComponent
                className={'min-h-[50px]'}
                title={'/property_assets/vehicles'}
                loading={loadingPersonVehiclesInformation}
                error={errorPersonVehiclesInformation}
                component={<RenderVehicles/>}
            />
        </div>
    )
}
